import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, Users, QrCode, LogIn, Building2, 
  UtensilsCrossed, Truck, Package, Clock, FileText, 
  Shield, UserCog, Settings, Menu, X, LogOut, ChevronDown, Smartphone
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { auth } from "@/lib/api";
import { toast } from "sonner";

const navigation = [
  { name: "Dashboard", href: "/", icon: LayoutDashboard, roles: ["admin", "operator", "user"] },
  { name: "Anagrafica", href: "/anagrafica", icon: Users, roles: ["admin"] },
  { name: "Gestione Badge", href: "/gestione-badge", icon: QrCode, highlight: "red", roles: ["admin"] },
  { name: "Controllo Accessi", href: "/controllo-accessi", icon: LogIn, highlight: "green", roles: ["admin"] },
  { name: "Gate Mobile", href: "/gate", icon: Smartphone, highlight: "blue", roles: ["admin", "operator"] },
  { name: "Gate Mensa", href: "/gate-mensa", icon: UtensilsCrossed, highlight: "orange", roles: ["admin", "operator"] },
  { name: "Organizzazioni", href: "/organizzazioni", icon: Building2, roles: ["admin"] },
  { name: "Cucine Campo", href: "/cucine-campo", icon: UtensilsCrossed, roles: ["admin"] },
  { name: "Veicoli", href: "/mezzi", icon: Truck, roles: ["admin"] },
  { name: "Risorse", href: "/risorse", icon: Package, roles: ["admin"] },
  { name: "Operazioni", href: "/operazioni", icon: Clock, roles: ["admin"] },
  { name: "Reportistica", href: "/reportistica", icon: FileText, roles: ["admin"] },
  { name: "Privacy GDPR", href: "/privacy", icon: Shield, roles: ["admin"] },
  { name: "Gestione Utenti", href: "/gestione-utenti", icon: UserCog, roles: ["admin"] },
  { name: "Impostazioni", href: "/impostazioni", icon: Settings, roles: ["admin"] },
];

export default function AppLayout({ children, user }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await auth.logout();
      navigate("/login");
    } catch (error) {
      toast.error("Errore durante il logout");
    }
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  // Filter navigation items based on user role
  const userRole = user?.role || "user";
  const filteredNavigation = navigation.filter(item => 
    !item.roles || item.roles.includes(userRole)
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="app-layout">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="h-16 flex items-center gap-3 px-4 border-b border-slate-200">
          <img 
            src="https://upload.wikimedia.org/wikipedia/commons/4/4e/Logo_Associazione_Nazionale_Carabinieri.gif"
            alt="ANC Logo"
            className="h-10 w-auto"
          />
          <div>
            <h1 className="text-sm font-bold text-[#0B1E3D] leading-tight">Campo ANC</h1>
            <p className="text-xs text-slate-500">Protezione Civile</p>
          </div>
          <button 
            className="lg:hidden ml-auto p-2"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-3 space-y-1 overflow-y-auto h-[calc(100vh-4rem)]" data-testid="sidebar-nav">
          {filteredNavigation.map((item) => {
            const isActive = location.pathname === item.href;
            let className = "sidebar-item";
            
            if (isActive) {
              className += " active";
            } else if (item.highlight === "red") {
              className += " highlight-red";
            } else if (item.highlight === "green") {
              className += " highlight-green";
            } else if (item.highlight === "blue") {
              className += " highlight-blue";
            }

            return (
              <Link
                key={item.name}
                to={item.href}
                className={className}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.href.replace("/", "") || "dashboard"}`}
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm font-medium">{item.name}</span>
              </Link>
            );
          })}
        </nav>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Header */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 sticky top-0 z-30">
          <button
            className="lg:hidden p-2 -ml-2"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-6 w-6" />
          </button>

          <div className="flex-1" />

          {/* User role badge */}
          <span className="hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700 mr-3 capitalize">
            {userRole}
          </span>

          {/* User menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-[#0B1E3D] text-white text-xs">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden sm:block text-sm font-medium">{user?.name}</span>
                <ChevronDown className="h-4 w-4 text-slate-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
                <p className="text-xs text-slate-400 capitalize mt-0.5">Ruolo: {userRole}</p>
              </div>
              <DropdownMenuSeparator />
              {userRole === "admin" && (
                <DropdownMenuItem asChild>
                  <Link to="/impostazioni" className="cursor-pointer">
                    <Settings className="mr-2 h-4 w-4" />
                    Impostazioni
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer" data-testid="logout-btn">
                <LogOut className="mr-2 h-4 w-4" />
                Esci
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Page content */}
        <main className="p-4 md:p-6 lg:p-8" data-testid="main-content">
          {children}
        </main>
      </div>
    </div>
  );
}
